import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  LinearProgress,
} from "@mui/material";
import {
  Add,
  Search,
  FilterList,
  MoreVert,
  Edit,
  Delete,
  Visibility,
  Devices as DevicesIcon,
  Wifi,
  WifiOff,
  Sync,
  Build,
  QrCode,
  Refresh,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { devicesAPI } from "../services/api";
import { useSnackbar } from "notistack";

const validationSchema = Yup.object({
  deviceName: Yup.string().required("Device name is required"),
  deviceType: Yup.string().required("Device type is required"),
  ipAddress: Yup.string()
    .matches(/^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/, "Invalid IP address")
    .required("IP address is required"),
  port: Yup.number()
    .min(1, "Port must be between 1 and 65535")
    .max(65535, "Port must be between 1 and 65535")
    .required("Port is required"),
  location: Yup.string().required("Location is required"),
  serialNumber: Yup.string().required("Serial number is required"),
});

const Devices = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [devices, setDevices] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [testingConnection, setTestingConnection] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [syncingDevice, setSyncingDevice] = useState(null);

  const loadDevices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await devicesAPI.getAll({
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm,
        type: filterType !== "all" ? filterType : undefined,
        status: filterStatus !== "all" ? filterStatus : undefined,
      });
      setDevices(response.data.devices || []);
    } catch (error) {
      enqueueSnackbar("Failed to load devices", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [
    enqueueSnackbar,
    page,
    rowsPerPage,
    searchTerm,
    filterType,
    filterStatus,
  ]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  const handleCreateDevice = async (values) => {
    try {
      if (editingDevice) {
        await devicesAPI.update(editingDevice.id, values);
        enqueueSnackbar("Device updated successfully", { variant: "success" });
      } else {
        await devicesAPI.create(values);
        enqueueSnackbar("Device created successfully", { variant: "success" });
      }
      setOpenDialog(false);
      setEditingDevice(null);
      loadDevices();
    } catch (error) {
      enqueueSnackbar("Failed to save device", { variant: "error" });
    }
  };

  const handleEditDevice = (device) => {
    setEditingDevice(device);
    formik.setValues({
      deviceName: device.deviceName,
      deviceType: device.deviceType,
      ipAddress: device.ipAddress,
      port: device.port,
      location: device.location,
      serialNumber: device.serialNumber,
      isActive: device.isActive,
      description: device.description || "",
    });
    setOpenDialog(true);
  };

  const handleDeleteDevice = async (deviceId) => {
    try {
      await devicesAPI.delete(deviceId);
      enqueueSnackbar("Device deleted successfully", { variant: "success" });
      loadDevices();
    } catch (error) {
      enqueueSnackbar("Failed to delete device", { variant: "error" });
    }
  };

  const handleTestConnection = async (deviceId) => {
    try {
      setTestingConnection(true);
      await devicesAPI.testConnection(deviceId);
      enqueueSnackbar("Connection test successful", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Connection test failed", { variant: "error" });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSyncDevice = async (deviceId) => {
    try {
      setSyncingDevice(deviceId);
      await devicesAPI.sync(deviceId);
      enqueueSnackbar("Device synced successfully", { variant: "success" });
      loadDevices();
    } catch (error) {
      enqueueSnackbar("Device sync failed", { variant: "error" });
    } finally {
      setSyncingDevice(null);
    }
  };

  const formik = useFormik({
    initialValues: {
      deviceName: "",
      deviceType: "",
      ipAddress: "",
      port: 8080,
      location: "",
      serialNumber: "",
      isActive: true,
      description: "",
    },
    validationSchema,
    onSubmit: handleCreateDevice,
  });

  const getStatusChip = (status) => {
    const statusConfig = {
      online: { color: "success", label: "Online" },
      offline: { color: "error", label: "Offline" },
      maintenance: { color: "warning", label: "Maintenance" },
    };
    const config = statusConfig[status] || statusConfig.offline;

    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "biometric":
        return <DevicesIcon />;
      case "rfid":
        return <QrCode />;
      case "mobile":
        return <DevicesIcon />;
      case "qr":
        return <QrCode />;
      default:
        return <DevicesIcon />;
    }
  };

  const handleMenuOpen = (event, device) => {
    setAnchorEl(event.currentTarget);
    setSelectedDevice(device);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDevice(null);
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Device Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setEditingDevice(null);
            formik.resetForm();
            setOpenDialog(true);
          }}
        >
          Add Device
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Wifi sx={{ fontSize: 40, color: "success.main", mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {devices.filter((d) => d.status === "online").length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Online Devices
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <WifiOff sx={{ fontSize: 40, color: "error.main", mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {devices.filter((d) => d.status === "offline").length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Offline Devices
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <DevicesIcon
                  sx={{ fontSize: 40, color: "primary.main", mr: 2 }}
                />
                <Box>
                  <Typography variant="h4">{devices.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Devices
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Sync sx={{ fontSize: 40, color: "info.main", mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {
                      devices.filter(
                        (d) =>
                          d.lastSync &&
                          new Date(d.lastSync) >
                            new Date(Date.now() - 24 * 60 * 60 * 1000)
                      ).length
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Synced Today
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search devices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  label="Type"
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="biometric">Biometric</MenuItem>
                  <MenuItem value="rfid">RFID</MenuItem>
                  <MenuItem value="mobile">Mobile</MenuItem>
                  <MenuItem value="qr">QR Code</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="online">Online</MenuItem>
                  <MenuItem value="offline">Offline</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={loadDevices}
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={loadDevices}
                >
                  Refresh
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSearchTerm("");
                    setFilterType("all");
                    setFilterStatus("all");
                  }}
                >
                  Clear
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Devices Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Device</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Sync</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.map((device) => (
                <TableRow key={device.id} hover>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box sx={{ mr: 2 }}>{getTypeIcon(device.deviceType)}</Box>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600 }}
                        >
                          {device.deviceName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {device.serialNumber}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={device.deviceType.toUpperCase()}
                      size="small"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell>{device.location}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {device.ipAddress}:{device.port}
                    </Typography>
                  </TableCell>
                  <TableCell>{getStatusChip(device.status)}</TableCell>
                  <TableCell>
                    {device.lastSync
                      ? new Date(device.lastSync).toLocaleString()
                      : "Never"}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={(e) => handleMenuOpen(e, device)}
                      size="small"
                    >
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={devices.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Card>

      {/* Add/Edit Device Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingDevice ? "Edit Device" : "Add New Device"}
        </DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Device Name"
                  name="deviceName"
                  value={formik.values.deviceName}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.deviceName &&
                    Boolean(formik.errors.deviceName)
                  }
                  helperText={
                    formik.touched.deviceName && formik.errors.deviceName
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Device Type</InputLabel>
                  <Select
                    name="deviceType"
                    value={formik.values.deviceType}
                    onChange={formik.handleChange}
                    label="Device Type"
                    error={
                      formik.touched.deviceType &&
                      Boolean(formik.errors.deviceType)
                    }
                  >
                    <MenuItem value="biometric">Biometric</MenuItem>
                    <MenuItem value="rfid">RFID</MenuItem>
                    <MenuItem value="mobile">Mobile</MenuItem>
                    <MenuItem value="qr">QR Code</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="IP Address"
                  name="ipAddress"
                  value={formik.values.ipAddress}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.ipAddress && Boolean(formik.errors.ipAddress)
                  }
                  helperText={
                    formik.touched.ipAddress && formik.errors.ipAddress
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Port"
                  name="port"
                  type="number"
                  value={formik.values.port}
                  onChange={formik.handleChange}
                  error={formik.touched.port && Boolean(formik.errors.port)}
                  helperText={formik.touched.port && formik.errors.port}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Location"
                  name="location"
                  value={formik.values.location}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.location && Boolean(formik.errors.location)
                  }
                  helperText={formik.touched.location && formik.errors.location}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Serial Number"
                  name="serialNumber"
                  value={formik.values.serialNumber}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.serialNumber &&
                    Boolean(formik.errors.serialNumber)
                  }
                  helperText={
                    formik.touched.serialNumber && formik.errors.serialNumber
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  multiline
                  rows={3}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      name="isActive"
                      checked={formik.values.isActive}
                      onChange={formik.handleChange}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingDevice ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            handleEditDevice(selectedDevice);
            handleMenuClose();
          }}
        >
          <Edit sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleTestConnection(selectedDevice.id);
            handleMenuClose();
          }}
        >
          <Build sx={{ mr: 1 }} />
          Test Connection
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleSyncDevice(selectedDevice.id);
            handleMenuClose();
          }}
        >
          <Sync sx={{ mr: 1 }} />
          Sync Device
        </MenuItem>
        <MenuItem
          onClick={() => {
            // Navigate to device details
            handleMenuClose();
          }}
        >
          <Visibility sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleDeleteDevice(selectedDevice.id);
            handleMenuClose();
          }}
        >
          <Delete sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Loading Overlay */}
      {testingConnection && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Testing Connection...
            </Typography>
            <LinearProgress />
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default Devices;
